import React, { useState } from 'react';
import { navigate, safeFetch } from "./App";
import { CheckCircle, AlertCircle, ArrowLeft, Users, Lock, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { memoryCache } from './cacheSafeFetch';

export default function CreateZoneForm({ onClose, chatId }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const isPrivate = true; // Zones are always private
    const [submitting, setSubmitting] = useState(false);
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [showSubmittingOverlay, setShowSubmittingOverlay] = useState(false);
    const [showCheckmark, setShowCheckmark] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [contactInfo, setContactInfo] = useState('');
    const [contactInfoError, setContactInfoError] = useState('');

    const tabsOrder = ['details']; // Removed privacy tab - zones are always private

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateCurrentTab = () => {
        if (activeTab === 'details') {
            if (!name.trim() || !description.trim() || !contactInfo.trim()) return false;
            if (!validateEmail(contactInfo)) return false;
            return true;
        }
        // Location is optional, so always return true
        return true;
    };

    const handleSubmit = async () => {
        setShowSubmittingOverlay(true);
        setShowCheckmark(false);
        setSubmitting(true);

        if (!name.trim() || !description.trim() || !contactInfo.trim()) {
            alert("נא למלא את כל השדות החיוניים");
            setShowSubmittingOverlay(false);
            setSubmitting(false);
            return;
        }

        try {
            // Create the zone
            const createRes = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/create-zone', chatId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chat_id: chatId,
                    zone_name: name.trim(),
                    description: description.trim(),
                    is_private: isPrivate,
                    contact_info: contactInfo
                })
            });

            const createData = await createRes.json();

            // Check if zone already exists (409 conflict)
            if (createRes.status === 409) {
                alert(createData.error || 'קהילה בשם זה כבר קיימת. אנא בחר שם אחר.');
                setShowSubmittingOverlay(false);
                setSubmitting(false);
                return;
            }

            // Check for other errors
            if (!createRes.ok) {
                alert('שגיאה ביצירת הקהילה. נסה שוב.');
                setShowSubmittingOverlay(false);
                setSubmitting(false);
                return;
            }

            // Invalidate the user-locals cache to force refresh
            const cacheKey = `user-locals-${chatId}`;
            if (memoryCache[cacheKey]) {
                delete memoryCache[cacheKey];
            }

            // Show checkmark animation
            setShowCheckmark(true);

            // After checkmark shows, hide overlay and show success message
            setTimeout(() => {
                setShowSubmittingOverlay(false);
                setShowCheckmark(false);
                setSubmissionComplete(true);
                setSubmitting(false);
            }, 1500);

        } catch (error) {
            console.error('Error creating zone:', error);
            alert('שגיאה ביצירת הקהילה. נסה שוב.');
            setShowSubmittingOverlay(false);
            setSubmitting(false);
        }
    };

    if (chatId === 'null' || chatId === null || chatId === '' || chatId === 'guest') {
        return (
            <div className="fixed top-20 left-0 right-0 z-50 px-4">
                <div className="bg-warning-100 border border-warning-500 p-4 rounded-xl shadow-md text-black text-center space-y-3 max-w-md mx-auto">
                    <h2 className="text-lg font-bold flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" /> יש להתחבר <AlertCircle className="w-5 h-5" /></h2>
                    <p className="text-sm">
                        כדי ליצור קהילה, נא להתחבר עם אחת מהאפשרויות:
                    </p>
                    <a
                        href="https://t.me/MekomitBot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-info-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-info-700 transition"
                    >
                        כניסה דרך טלגרם
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen overflow-y-auto bg-surface pb-24">
            {/* Submitting Overlay */}
            <AnimatePresence>
                {showSubmittingOverlay && (
                    <motion.div
                        key="submitting-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm"
                        style={{ pointerEvents: 'all' }}
                    >
                        <div className="flex flex-col items-center justify-center">
                            <div className="relative flex items-center justify-center">
                                {!showCheckmark && (
                                    <svg className="w-36 h-36 absolute animate-spin-slow" viewBox="0 0 120 120">
                                        <circle
                                            cx="60" cy="60" r="52"
                                            fill="none"
                                            stroke="#10b981"
                                            strokeWidth="8"
                                            strokeDasharray="326"
                                            strokeDashoffset="60"
                                            strokeLinecap="round"
                                            opacity="0.7"
                                        />
                                    </svg>
                                )}
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className="flex items-center justify-center w-32 h-32 rounded-full bg-white shadow-xl"
                                    style={{ zIndex: 2 }}
                                >
                                    {showCheckmark ? (
                                        <motion.div
                                            initial={{ scale: 0.7, opacity: 0 }}
                                            animate={{ scale: 1.1, opacity: 1 }}
                                            exit={{ scale: 0.7, opacity: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <CheckCircle className="w-24 h-24 text-success-500 animate-pop" strokeWidth={2.5} />
                                        </motion.div>
                                    ) : (
                                        <Users className="w-24 h-24 text-success-500 animate-fadeInUp" strokeWidth={2.5} />
                                    )}
                                </motion.div>
                            </div>
                            <div className="mt-8 text-center text-white drop-shadow-lg animate-fadeInUp">
                                {showCheckmark ? (
                                    <div className="text-2xl font-bold">הקהילה נוצרה בהצלחה!</div>
                                ) : (
                                    <div className="text-2xl font-bold">יוצר את הקהילה שלך...</div>
                                )}
                            </div>
                        </div>
                        <style>{`
                            @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                            .animate-spin-slow { animation: spin-slow 2.2s linear infinite; }
                            @keyframes fadeInUp { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
                            .animate-fadeInUp { animation: fadeInUp 1.2s cubic-bezier(0.4,0,0.2,1) both; }
                            @keyframes pop { 0% { transform: scale(0.7); opacity: 0; } 80% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
                            .animate-pop { animation: pop 0.7s cubic-bezier(0.4,0,0.2,1) both; }
                        `}</style>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Form */}
            <AnimatePresence>
                {chatId !== 'null' && !submissionComplete && (
                    <motion.form
                        key="zone-form"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className="bg-surface text-ink-strong rounded-3xl shadow-xl mt-10 max-w-lg mx-auto px-4 py-8 space-y-6 relative"
                        style={{ boxShadow: '0 4px 32px 0 #e7e3d8' }}
                        onSubmit={e => { e.preventDefault(); }}
                    >
                        <div className="text-center mb-6">
                            <div className="flex justify-center mb-3">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-success-500 to-success-600 flex items-center justify-center shadow-lg">
                                    <Users className="w-8 h-8 text-white" strokeWidth={2.5} />
                                </div>
                            </div>
                            <h2 className="text-2xl font-extrabold text-ink-strong">יצירת קהילה פרטית</h2>
                            <p className="text-sm text-ink-muted mt-2">צור מרחב פרטי המבוסס על הזמנות לקהילה שלך</p>
                            <div className="mt-4 p-4 bg-brand-50 border border-brand-200 rounded-xl">
                                <div className="flex items-start gap-2 mb-3">
                                    <Lock className="w-5 h-5 text-brand-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                                    <div>
                                        <p className="text-sm text-brand-800 font-semibold mb-1">
                                            קהילות פרטיות מבוססות הזמנות בלבד
                                        </p>
                                        <p className="text-sm text-brand-700">
                                            רק משתמשים שקיבלו הזמנה יוכלו לראות ולהצטרף לקהילה שלך
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2 pt-3 border-t border-brand-200">
                                    <Globe className="w-5 h-5 text-info-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                                    <p className="text-sm text-info-700">
                                        <strong>קהילות מבוססות מיקום</strong> זמינות בעת יצירת עסק, מפגש או בארטר
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="bg-surface-sunken h-2 rounded-full mb-4">
                            <motion.div
                                className="bg-success-500 h-2 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${((tabsOrder.indexOf(activeTab) + 1) / tabsOrder.length) * 100}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </div>

                        {/* Details Tab */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'details' && (
                                <motion.div
                                    key="details"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-6"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1">פרטי הקהילה</div>

                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">שם הקהילה *</label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="input-modern"
                                            placeholder="לדוגמה: קהילת תל אביב הירוקה"
                                        />
                                        <div className="text-xs text-ink-subtle mt-1">בחר שם ברור ומזמין לקהילה</div>
                                    </div>

                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">תיאור הקהילה *</label>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="input-modern min-h-[100px]"
                                            rows="4"
                                            placeholder="ספר על הקהילה - מה המטרה שלה, מי חברי הקהילה, ומה מיוחד בה"
                                        />
                                        <div className="text-xs text-ink-subtle mt-1">ספרו בקצרה על הקהילה ומטרותיה</div>
                                    </div>

                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">אימייל ליצירת קשר *</label>
                                        <input
                                            type="email"
                                            value={contactInfo}
                                            onChange={(e) => {
                                                setContactInfo(e.target.value);
                                                if (!validateEmail(e.target.value)) {
                                                    setContactInfoError('נא להזין כתובת אימייל תקינה');
                                                } else {
                                                    setContactInfoError('');
                                                }
                                            }}
                                            className={`input-modern${contactInfoError ? ' border-danger-500' : ''}`}
                                            placeholder="your@email.com"
                                        />
                                        {contactInfoError && (
                                            <div className="text-xs text-danger-500 mt-1">{contactInfoError}</div>
                                        )}
                                        <div className="text-xs text-ink-subtle mt-1">פרטי קשר לאימות בלבד</div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>


                        {/* Navigation Buttons */}
                        <div className="flex gap-2 mt-4">
                            {tabsOrder.indexOf(activeTab) > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentIndex = tabsOrder.indexOf(activeTab);
                                        setActiveTab(tabsOrder[currentIndex - 1]);
                                    }}
                                    className="w-full bg-line text-ink-strong py-3 rounded-full font-semibold shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200"
                                >
                                    חזור
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    const currentIndex = tabsOrder.indexOf(activeTab);
                                    const isLastTab = currentIndex === tabsOrder.length - 1;

                                    if (!validateCurrentTab()) {
                                        alert('נא למלא את כל השדות החיוניים לפני מעבר הלאה');
                                        return;
                                    }

                                    if (isLastTab) {
                                        handleSubmit();
                                    } else {
                                        setActiveTab(tabsOrder[currentIndex + 1]);
                                    }
                                }}
                                className="w-full bg-success-500 text-white py-3 rounded-full font-bold shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-success-100 flex justify-center items-center gap-2"
                                disabled={submitting}
                            >
                                {submitting && tabsOrder.indexOf(activeTab) === tabsOrder.length - 1 ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        שולח...
                                    </>
                                ) : (
                                    tabsOrder.indexOf(activeTab) === tabsOrder.length - 1 ? 'צור קהילה' : 'הבא'
                                )}
                            </button>
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>

            {/* Success State */}
            <AnimatePresence>
                {submissionComplete && (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.5 }}
                        className="bg-gradient-to-br from-success-50 to-success-50 border border-success-100 text-success-700 p-8 rounded-3xl shadow-xl text-center space-y-6 mt-10 max-w-lg mx-auto relative"
                    >
                        <div className="flex justify-center mb-2">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-success-500 to-success-600 flex items-center justify-center shadow-lg">
                                <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
                            </div>
                        </div>
                        <h2 className="text-3xl font-extrabold text-ink-strong">הקהילה נוצרה בהצלחה!</h2>

                        <div className="bg-white rounded-2xl p-6 shadow-md space-y-4 text-right">
                            <h3 className="text-lg font-bold text-ink-strong flex items-center gap-2">
                                <Users className="w-5 h-5 text-brand-600" strokeWidth={2.5} />
                                מה הלאה?
                            </h3>
                            <div className="space-y-3 text-sm text-ink">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-brand-600 font-bold text-xs">1</span>
                                    </div>
                                    <p>
                                        <strong className="text-brand-600">הזמן חברים:</strong> לאחר האישור, תוכל למצוא קישור הזמנה בפרופיל שלך → קהילות
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-info-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-info-600 font-bold text-xs">2</span>
                                    </div>
                                    <p>
                                        <strong className="text-info-600">שתף תוכן:</strong> אתה וחברי הקהילה תוכלו להציג עסקים, מפגשים ובארטרים בקהילה
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-success-600 font-bold text-xs">3</span>
                                    </div>
                                    <p>
                                        <strong className="text-success-600">צמיחה ביחד:</strong> בנה קהילה תומכת שבה כולם יכולים להכיר ולעזור אחד לשני
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('zones')}
                            className="bg-gradient-to-r from-success-500 to-success-600 text-white font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-[2px] transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
                        >
                            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                            חזרה לקהילות
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Input styles */}
            <style>{`
                .input-modern {
                    background: #fff;
                    border: none;
                    border-radius: 1rem;
                    box-shadow: 0 1px 4px 0 #ece7de;
                    padding: 0.75rem 1rem;
                    font-family: 'Inter', 'Rubik', 'Nunito', sans-serif;
                    font-size: 1rem;
                    color: #222;
                    font-weight: 500;
                    outline: none;
                    transition: box-shadow 0.2s, background 0.2s;
                }
                .input-modern:focus {
                    background: #F4F2EF;
                    box-shadow: 0 2px 8px 0 #e7e3d8;
                }
            `}</style>
        </div>
    );
}
